import { jsx as _jsx } from "react/jsx-runtime";
/**
 * A player's avatar (shared `types/avatar.ts`) and the grid players pick one
 * from. The admin site, the player client and the overlay windows all use them.
 *
 * The avatar's SVG is fetched once (served art: through the Electron disk cache,
 * or from the API origin on the admin site) and drawn inline, not as an `<img>`,
 * so @swtorbit/ui's .player-avatar can colour it: its own colours, or a team's
 * tone. Avatar art may hold only plain shapes painted by colour-slot classes
 * (publish-game-art.ts checks it before it is published); anything that does not
 * look like that is not drawn.
 */
import { useEffect, useState } from 'react';
import { getPlayerAvatarUrl } from '@swtorbit/shared-assets';
import { PLAYER_AVATAR_IDS, PLAYER_AVATAR_NAMES, getAvatarHolder, getPlayerDisplayName, isPlayerAvatarId, } from '@swtorbit/shared-socket-events';
const UNSAFE_SVG = /<script|<foreignObject|\son[a-z]+\s*=|\shref\s*=/i;
/** Loaded SVG text by URL, and the loads still in flight. */
const loaded = new Map();
const loading = new Map();
function loadSvg(url) {
    let load = loading.get(url);
    if (!load) {
        load = fetch(url)
            .then((response) => {
            if (!response.ok)
                throw new Error(`Avatar ${url}: HTTP ${response.status}`);
            return response.text();
        })
            .then((text) => {
            const svg = text.trim();
            if (!svg.startsWith('<svg') || UNSAFE_SVG.test(svg))
                throw new Error(`Avatar ${url} is not plain SVG`);
            loaded.set(url, svg);
            return svg;
        })
            .finally(() => loading.delete(url));
        loading.set(url, load);
    }
    return load;
}
/**
 * Load these avatars' SVGs ahead of drawing them (a page that is screenshotted
 * waits for this). Settles when each has loaded or failed.
 */
export async function preloadPlayerAvatars(avatarIds) {
    const urls = [...new Set(avatarIds.filter(isPlayerAvatarId).map((id) => getPlayerAvatarUrl(id)))];
    await Promise.allSettled(urls.filter((url) => !loaded.has(url)).map(loadSvg));
}
/** The avatar's SVG markup once loaded (immediately when already cached). */
function useAvatarSvg(avatarId) {
    const url = avatarId ? getPlayerAvatarUrl(avatarId) : undefined;
    const [, setLoadedUrl] = useState();
    useEffect(() => {
        if (!url || loaded.has(url))
            return;
        let live = true;
        loadSvg(url)
            .then(() => live && setLoadedUrl(url))
            .catch(() => {
            // Leave the plain disc; the avatar is tried again when next mounted.
        });
        return () => {
            live = false;
        };
    }, [url]);
    return url ? loaded.get(url) : undefined;
}
export function PlayerAvatar({ avatarId, tone, size, label, className }) {
    const id = isPlayerAvatarId(avatarId) ? avatarId : undefined;
    const svg = useAvatarSvg(id);
    const classes = [
        'player-avatar',
        size && `player-avatar--${size}`,
        id ? `player-avatar--${id}` : 'player-avatar--empty',
        tone && `player-avatar--tone-${tone}`,
        className,
    ]
        .filter(Boolean)
        .join(' ');
    const a11y = label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true };
    return svg ? (_jsx("span", { className: classes, ...a11y, dangerouslySetInnerHTML: { __html: svg } })) : (_jsx("span", { className: classes, ...a11y }));
}
/**
 * Every avatar in a grid, as in a game's character select: the player's own is
 * picked out, and ones a teammate holds (or anyone, while either of you is on no
 * team) are greyed out with the holder's name on hover.
 */
export function AvatarPicker({ room, playerId, tone, disabled, onPick }) {
    const { players } = room;
    const own = players.find((p) => p.playerId === playerId)?.avatarId;
    return (_jsx("div", { className: "avatar-picker", children: _jsx("div", { className: "avatar-picker__grid", role: "group", "aria-label": "Choose your avatar", children: PLAYER_AVATAR_IDS.map((id) => {
                const holder = getAvatarHolder(room, id, playerId);
                const selected = own === id;
                const name = PLAYER_AVATAR_NAMES[id];
                const title = holder ? `${name}: taken by ${getPlayerDisplayName(holder.playerId, players)}` : selected ? `${name} (yours)` : name;
                return (_jsx("button", { type: "button", className: `avatar-picker__option${selected ? ' is-selected' : ''}${holder ? ' is-taken' : ''}`, title: title, "aria-label": title, "aria-pressed": selected, disabled: disabled || !!holder, onClick: () => !selected && onPick(id), children: _jsx(PlayerAvatar, { avatarId: id, tone: tone }) }, id));
            }) }) }));
}
